(() => {
  if (customElements.get('pf-smart-search')) return;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[’']/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  const aliases = { dames: 'vrouwen', vrouw: 'vrouwen', female: 'vrouwen', women: 'vrouwen', heren: 'mannen', man: 'mannen', male: 'mannen', men: 'mannen', vanilla: 'vanille', woody: 'houtachtig', fresh: 'fris', floral: 'bloemig', sweet: 'zoet', spicy: 'kruidig', aqua: 'acqua' };
  const words = value => normalize(value).split(/\s+/).filter(Boolean).map(word => aliases[word] || word);
  function distance(a, b) {
    const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
    for (let j = 0; j <= b.length; j++) d[0][j] = j;
    for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++) {
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
    return d[a.length][b.length];
  }
  function prepare(item) {
    item.reference = item.reference.replace(/^(geïnspireerd op|inspired by)\s+/i, '').trim();
    item.fields = [[item.title, 5], [item.reference, 4], [item.notes, 2], [item.audience, 2], [item.keywords, 3]].map(([value, weight]) => ({ tokens: words(value), phrase: words(value).join(' '), weight }));
    return item;
  }
  function score(item, query) {
    const tokens = words(query);
    if (!tokens.length) return 0;
    const phrase = tokens.join(' ');
    let total = 0;
    for (const token of tokens) {
      let best = 0;
      for (const field of item.fields) for (const candidate of field.tokens) {
        let match = 0;
        if (candidate === token) match = 10;
        else if (token.length >= 2 && candidate.startsWith(token)) match = 7;
        else if (token.length >= 4 && candidate.length >= 4 && !/\d/.test(token + candidate)) {
          const limit = Math.min(token.length, candidate.length) >= 7 ? 2 : 1;
          if (Math.abs(token.length - candidate.length) <= limit && distance(token, candidate) <= limit) match = 4;
        }
        best = Math.max(best, match * field.weight);
      }
      if (!best) {
        const compact = phrase.replace(/ /g, '');
        if (item.fields.some(field => field.phrase.replace(/ /g, '') === compact)) return 80;
        return 0;
      }
      total += best;
    }
    for (const field of item.fields) if (field.phrase === phrase) total += 100 * field.weight;
    return total;
  }
  let cataloguePromise;
  async function catalogue(endpoint) {
    const products = new Map();
    let url = new URL(endpoint, location.origin);
    const seen = new Set();
    while (url) {
      if (url.origin !== location.origin || seen.has(url.href)) throw new Error('Invalid index pagination');
      seen.add(url.href);
      const response = await fetch(url.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Index unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!doc.querySelector('[data-pf-search-index]')) throw new Error('Index missing');
      for (const element of doc.querySelectorAll('[data-pf-product]')) {
        const item = { ...element.dataset };
        if (item.id === '10200543068488' || normalize(item.title) === 'care' || normalize(item.title) === 'care plus') continue;
        const productUrl = new URL(item.url, location.origin);
        if (productUrl.origin !== location.origin || !productUrl.pathname.includes('/products/')) continue;
        products.set(item.id, prepare(item));
      }
      const next = doc.querySelector('[data-pf-next]');
      url = next ? new URL(next.getAttribute('href'), url) : null;
      if (url) url.searchParams.set('view', 'pf-search-index');
    }
    return [...products.values()];
  }
  customElements.define('pf-smart-search', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.input = this.querySelector('input');
      this.results = this.querySelector('ul');
      this.status = this.querySelector('[role="status"]');
      this.version = 0;
      this.input.addEventListener('input', () => { clearTimeout(this.timer); this.version++; this.timer = setTimeout(() => this.search(), 140); }, { signal: this.controller.signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') { this.input.value = ''; this.version++; this.clear(); this.input.focus(); }
        if (event.key === 'ArrowDown' && event.target === this.input && !this.results.hidden) { event.preventDefault(); this.results.querySelector('a')?.focus(); }
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.version++; this.controller?.abort(); clearTimeout(this.timer); }
    clear() { this.results.replaceChildren(); this.results.hidden = true; this.status.textContent = ''; }
    async search() {
      const query = this.input.value.trim();
      const version = this.version;
      this.clear();
      if (normalize(query).length < 2) { if (query) this.status.textContent = 'Typ minimaal 2 tekens.'; return; }
      this.status.textContent = 'Parfums zoeken…';
      try {
        if (!cataloguePromise) cataloguePromise = catalogue(this.dataset.endpoint).catch(error => { cataloguePromise = null; throw error; });
        const items = await cataloguePromise;
        if (!this.isConnected || version !== this.version) return;
        const matches = items.map(item => ({ item, rank: score(item, query) })).filter(result => result.rank > 0).sort((a, b) => b.rank - a.rank || a.item.title.localeCompare(b.item.title, 'nl'));
        this.status.textContent = matches.length ? `${matches.length} ${matches.length === 1 ? 'parfum gevonden' : 'parfums gevonden'}` : 'Geen passende parfums gevonden. Probeer een kortere naam of een geurwoord, zoals fris of vanille.';
        const fragment = document.createDocumentFragment();
        for (const { item } of matches) {
          const li = document.createElement('li');
          const a = document.createElement('a'); a.href = item.url;
          const title = document.createElement('strong'); title.textContent = item.title;
          a.append(title);
          const detail = document.createElement('span');
          if (item.reference) {
            const reference = document.createElement('strong');
            reference.className = 'pfss__reference';
            reference.textContent = `Geïnspireerd op ${item.reference}`;
            detail.append(reference);
          }
          const description = [item.notes, item.audience].filter(Boolean).join(' · ');
          if (description) detail.append(document.createTextNode((item.reference ? ' · ' : '') + description));
          a.append(detail); li.append(a); fragment.append(li);
        }
        this.results.replaceChildren(fragment); this.results.hidden = !matches.length;
      } catch { if (version === this.version) this.status.textContent = 'Zoeken is tijdelijk niet beschikbaar. Probeer het opnieuw of bekijk de parfums hieronder.'; }
    }
  });
})();
